export type DicomErrorType =
  'network' | 'file-format' | 'rendering' | 'initialization' | 'unknown';

export abstract class DicomBaseError extends Error implements DicomError {
  abstract type: DicomErrorType;
  originalError: unknown;
  readonly __isDicomError = true;

  constructor(message: string, originalError?: unknown) {
    super(message);
    this.originalError = originalError;
    // Restore prototype chain
    Object.setPrototypeOf(this, new.target.prototype);
  }
}

export class DicomInitializationError extends DicomBaseError {
  type = 'initialization' as const;
  constructor(message: string, originalError?: any) {
    super(message, originalError);
    this.name = 'DicomInitializationError';
  }
}

class DicomNetworkError extends DicomBaseError {
  type = 'network' as const;
  constructor(message: string, originalError?: any) {
    super(message, originalError);
    this.name = 'DicomNetworkError';
  }
}

class DicomFileFormatError extends DicomBaseError {
  type = 'file-format' as const;
  constructor(message: string, originalError?: any) {
    super(message, originalError);
    this.name = 'DicomFileFormatError';
  }
}

class DicomRenderingError extends DicomBaseError {
  type = 'rendering' as const;
  constructor(message: string, originalError?: any) {
    super(message, originalError);
    this.name = 'DicomRenderingError';
  }
}

class DicomUnknownError extends DicomBaseError {
  type = 'unknown' as const;
  constructor(message: string, originalError?: any) {
    super(message, originalError);
    this.name = 'DicomUnknownError';
  }
}

export type DicomError = {
  type: DicomErrorType;
  message: string;
  originalError?: any;
  __isDicomError: true;
};

/**
 * Analyzes an error and returns a corresponding custom DICOM error.
 * This is used to classify errors originating from external libraries.
 */
function classifyError(error: any): DicomError {
  const errorMessage = String(
    error?.message ?? error ?? 'Unknown error'
  ).toLowerCase();

  const errorClassifiers = [
    {
      keywords: [
        'network',
        'fetch',
        'connection',
        'timeout',
        'net::',
        'cors',
        'cross-origin',
        'access-control-allow-origin',
        '404',
        '500',
      ],
      ErrorClass: DicomNetworkError,
      message: 'A network error occurred while loading the DICOM file.',
    },
    {
      keywords: ['invalid', 'parse', 'format', 'dicom', 'dcm', 'decode'],
      ErrorClass: DicomFileFormatError,
      message: 'The file is not a valid or supported DICOM format.',
    },
    {
      keywords: ['render', 'viewport', 'canvas', 'webgl', 'stack'],
      ErrorClass: DicomRenderingError,
      message: 'An error occurred while rendering the DICOM image.',
    },
    {
      keywords: ['initialization', 'init', 'engine'],
      ErrorClass: DicomInitializationError,
      message: 'Failed to initialize the DICOM viewer.',
    },
  ];

  for (const {keywords, ErrorClass, message} of errorClassifiers) {
    if (keywords.some(keyword => errorMessage.includes(keyword))) {
      return new ErrorClass(message, error);
    }
  }

  return new DicomUnknownError('An unexpected error occurred.', error);
}

export const createDicomError = (error: unknown): DicomError => {
  if (
    error &&
    typeof error === 'object' &&
    '__isDicomError' in error &&
    error.__isDicomError === true
  ) {
    return error as DicomError;
  }

  return classifyError(error);
};
